// QuizPage.tsx - Updated to use Quiz with state management
import { useMemo, useState, useCallback } from "react";
import { 
  Sparkles, 
  Users, 
  GraduationCap, 
  ChevronDown, 
  Award,
  UserPlus,
  X,
} from "lucide-react";
import Quiz from "./Quiz"; // Import the updated Quiz component
import islamData from "./quizdata/islam.json";
import studentsData from "./quizdata/students.json";
import { useMyDialog } from "@/context/MyDialogProvider";
import StartQuizDialog from "./StartQuizDialog";
import { QuizTopic, IslamQuizData, QuizResult, Student, StudentWithGrade } from "./types";
import { TopicCard } from "./TopicCard";
import { QuizNavbar } from "./QuizNavbar";
import { Certificate } from "./Certificate";
import QuizHistory from "./QuizHistory";
import MyDialogButton from "@/MY_COMPONENTS/MyDialogButton";
import StudentManagement from "./StudentManagement";
import { MyFlexWrapBox } from "@/MY_COMPONENTS/MyFlexWrapBox";

// Generate unique ID
const generateId = () => `STU-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;

function shuffle<T>(array: T[]): T[] {
  return [...array].sort(() => Math.random() - 0.5);
}

export default function QuizHome() {
  const { open, closeAll } = useMyDialog();
  const data = islamData as IslamQuizData;

  // ============ SHARED STATE ============
  // Students
  const [students, setStudents] = useState<Student[]>(() => {
    const saved = localStorage.getItem("students");
    return saved ? JSON.parse(saved) : studentsData.students;
  });

  // Quiz History
  const [history, setHistory] = useState<StudentWithGrade[]>(() => {
    const saved = localStorage.getItem("studentQuizHistory");
    return saved ? JSON.parse(saved) : [];
  });

  // Quiz Config
  const [quizConfig, setQuizConfig] = useState({
    studentName: "",
    started: false,
    selectedTopicId: data.topics[0]?.id || "",
    selectedStudentId: "",
  });

  // Quiz State (for the active quiz)
  const [quizState, setQuizState] = useState({
    currentQuestion: 0,
    selected: null as string | null,
    score: 0,
    showAnswer: false,
    finished: false,
  });

  // Add student form
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({
    name: "",
    grade: "",
    section: "",
    email: "",
    phone: "",
    enrollmentDate: new Date().toISOString().split('T')[0],
  });

  // ============ COMPUTED ============
  const selectedTopic = useMemo(() => 
    data.topics.find(t => t.id === quizConfig.selectedTopicId) || data.topics[0],
    [quizConfig.selectedTopicId, data.topics]
  );

  const selectedStudent = useMemo(() => 
    students.find(s => s.id === quizConfig.selectedStudentId),
    [quizConfig.selectedStudentId, students]
  );

  // Quiz questions (shuffled)
  const quizQuestions = useMemo(() => {
    if (!selectedTopic?.questions) return [];
    return shuffle(selectedTopic.questions);
  }, [selectedTopic]);

  // Quiz computed values
  const percentage = useMemo(() => {
    if (quizQuestions.length === 0) return 0;
    return Math.round((quizState.score / quizQuestions.length) * 100);
  }, [quizState.score, quizQuestions.length]);

  const passed = percentage >= (selectedTopic?.passingPercentage || 60);

  // ============ PERSISTENCE ============
  const saveStudents = useCallback((newStudents: Student[]) => {
    localStorage.setItem("students", JSON.stringify(newStudents));
    setStudents(newStudents);
  }, []);

  const saveHistory = useCallback((newHistory: StudentWithGrade[]) => {
    localStorage.setItem("studentQuizHistory", JSON.stringify(newHistory));
    setHistory(newHistory);
  }, []);

  // ============ QUIZ HANDLERS ============
  const handleQuizSelect = useCallback((option: string) => {
    if (quizState.showAnswer) return;

    const currentQuestion = quizQuestions[quizState.currentQuestion];
    setQuizState((prev) => ({
      ...prev,
      selected: option,
      showAnswer: true,
      score: prev.score + (option === currentQuestion?.answer ? 1 : 0),
    }));
  }, [quizState.showAnswer, quizState.currentQuestion, quizQuestions]);

  const handleQuizNext = useCallback(() => {
    const isLast = quizState.currentQuestion + 1 >= quizQuestions.length;

    if (isLast) {
      const result: QuizResult = {
        score: quizState.score,
        total: quizQuestions.length,
        percentage,
        passed,
        correctAnswers: quizState.score,
        totalQuestions: quizQuestions.length,
      };

      setQuizState((prev) => ({
        ...prev,
        finished: true,
      }));

      // Save result to history
      if (quizConfig.selectedStudentId) {
        const newRecord: StudentWithGrade = {
          studentId: quizConfig.selectedStudentId,
          studentName: selectedStudent?.name || "Unknown",
          grade: selectedStudent?.grade || "N/A",
          topicId: quizConfig.selectedTopicId,
          score: percentage,
          passed,
          completedAt: new Date().toISOString(),
          correctAnswers: quizState.score,
          totalQuestions: quizQuestions.length,
        };

        const filtered = history.filter(
          h => !(h.studentId === quizConfig.selectedStudentId && h.topicId === quizConfig.selectedTopicId)
        );
        saveHistory([...filtered, newRecord]);
      }

      return;
    }

    setQuizState((prev) => ({
      ...prev,
      currentQuestion: prev.currentQuestion + 1,
      selected: null,
      showAnswer: false,
    }));
  }, [quizState, quizQuestions, percentage, passed, quizConfig, selectedStudent, history, saveHistory]);

  const handleQuizRestart = useCallback(() => {
    setQuizState({
      currentQuestion: 0,
      selected: null,
      score: 0,
      showAnswer: false,
      finished: false,
    });
  }, []);

  // ============ STUDENT HANDLERS ============
  const handleAddStudent = useCallback(() => {
    if (!newStudent.name.trim() || !newStudent.grade.trim()) {
      alert("Please enter name and grade");
      return;
    }

    const student: Student = {
      id: generateId(),
      name: newStudent.name.trim(),
      grade: newStudent.grade.trim(),
      section: newStudent.section.trim() || undefined,
      email: newStudent.email.trim() || undefined,
      phone: newStudent.phone.trim() || undefined,
      enrollmentDate: newStudent.enrollmentDate || new Date().toISOString().split('T')[0],
      guardian: { name: "", phone: "", email: "" }
    };

    const updated = [...students, student];
    saveStudents(updated);
    
    setNewStudent({
      name: "",
      grade: "",
      section: "",
      email: "",
      phone: "",
      enrollmentDate: new Date().toISOString().split('T')[0],
    });
    setShowAddStudent(false);
    
    setQuizConfig(prev => ({
      ...prev,
      selectedStudentId: student.id,
      studentName: student.name
    }));
  }, [newStudent, students, saveStudents]);

  const handleStudentSelect = useCallback((studentId: string) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setQuizConfig(prev => ({ 
        ...prev, 
        selectedStudentId: studentId,
        studentName: student.name 
      }));
    }
  }, [students]);

  // ============ QUIZ NAVIGATION ============
  const getStudentGrade = useCallback((studentId: string, topicId: string) => {
    return history.find(h => h.studentId === studentId && h.topicId === topicId);
  }, [history]);

  const getStudentGrades = useCallback((studentId: string) => {
    return history.filter(h => h.studentId === studentId);
  }, [history]);

  const handleStartQuiz = useCallback((topic: QuizTopic) => {
    setQuizConfig(prev => ({ ...prev, selectedTopicId: topic.id }));
    
    open({
      header: "",
      variant: "modal",
      content: (
        <StartQuizDialog
          selectedTopic={topic}
          studentName={quizConfig.studentName}
          setStudentName={(name) => setQuizConfig(prev => ({ ...prev, studentName: name }))}
          onClose={closeAll}
          onStart={(name) => {
            setQuizConfig(prev => ({ ...prev, studentName: name, started: true }));
            // Reset quiz state when starting
            setQuizState({
              currentQuestion: 0,
              selected: null,
              score: 0,
              showAnswer: false,
              finished: false,
            });
            closeAll();
          }}
          students={students}
          selectedStudentId={quizConfig.selectedStudentId}
          onStudentSelect={(id) => {
            const student = students.find(s => s.id === id);
            if (student) {
              setQuizConfig(prev => ({ 
                ...prev, 
                selectedStudentId: id,
                studentName: student.name 
              }));
            }
          }}
        />
      ),
    });
  }, [open, closeAll, quizConfig.studentName, quizConfig.selectedStudentId, students]);

  const handleChangeTopic = useCallback(() => {
    setQuizConfig(prev => ({ ...prev, started: false }));
    // Reset quiz state when leaving quiz
    setQuizState({
      currentQuestion: 0,
      selected: null,
      score: 0,
      showAnswer: false,
      finished: false,
    });
  }, []);

  // ============ VIEWS ============
  const handleViewCertificate = useCallback((record: StudentWithGrade) => {
    const topic = data.topics.find(t => t.id === record.topicId);
    if (!topic) return;

    open({
      header: "",
      variant: "modal",
      content: (
        <div className="p-6 max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">🎓 Certificate</h2>
            <button onClick={() => window.print()} className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition">
              Print
            </button>
          </div>
          <Certificate
            title={topic.title}
            studentName={record.studentName}
            percentage={record.score}
            grade={record.grade}
            topicIcon={topic.icon || "🏆"}
            certificateId={`CERT-${record.studentId.slice(-4)}-${record.topicId.slice(0, 4)}`}
            issueDate={record.completedAt}
            passed={record.passed}
            correctAnswers={record.correctAnswers}
            totalQuestions={record.totalQuestions}
          />
          <div className="mt-6 flex justify-center">
            <button onClick={closeAll} className="px-6 py-2 bg-gray-200 rounded-xl hover:bg-gray-300 transition">
              Close
            </button>
          </div>
        </div>
      ),
    });
  }, [data.topics, open, closeAll]);

  // ============ RENDER ============
// In QuizPage.tsx - Updated to pass grade and topicIcon to Quiz

if (quizConfig.started) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <QuizNavbar
        selectedTopic={selectedTopic}
        studentName={quizConfig.studentName}
        onChangeTopic={handleChangeTopic}
      />
      <div className="py-6">
        <Quiz
          title={selectedTopic.title}
          description={selectedTopic.description}
          studentName={quizConfig.studentName}
          passingPercentage={selectedTopic.passingPercentage}
          questions={quizQuestions}
          primaryColor={selectedTopic.color}
          showExplanation={true}
          quizState={quizState}
          percentage={percentage}
          passed={passed}
          onSelect={handleQuizSelect}
          onNext={handleQuizNext}
          onRestart={handleQuizRestart}
          grade={selectedStudent?.grade}
          topicIcon={selectedTopic.icon || "🏆"}
        />
      </div>
    </div>
  );
}

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <HeroSection />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        {/* Student Selector */}
        <StudentSelector
          students={students}
          selectedStudentId={quizConfig.selectedStudentId}
          selectedStudent={selectedStudent}
          onStudentSelect={handleStudentSelect}
          showAddStudent={showAddStudent}
          setShowAddStudent={setShowAddStudent}
          newStudent={newStudent}
          setNewStudent={setNewStudent}
          onAddStudent={handleAddStudent}
        />

        {/* Student Grades Overview */}
        {selectedStudent && history.length > 0 && (
          <StudentGradesOverview
            grades={getStudentGrades(selectedStudent.id)}
            topics={data.topics}
            onViewCertificate={handleViewCertificate}
          />
        )}

        {/* Topic Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-7">
          {data.topics.map((topic) => {
            const studentGrade = quizConfig.selectedStudentId 
              ? getStudentGrade(quizConfig.selectedStudentId, topic.id)
              : null;
            
            return (
              <TopicCard
                key={topic.id}
                topic={topic}
                isSelected={quizConfig.selectedTopicId === topic.id}
                onSelect={() => setQuizConfig(prev => ({ ...prev, selectedTopicId: topic.id }))}
                onStart={() => handleStartQuiz(topic)}
                studentGrade={studentGrade}
                onViewCertificate={studentGrade?.passed ? () => handleViewCertificate(studentGrade) : undefined}
              />
            );
          })}
        </div>

        {/* Action Buttons */}
        <MyFlexWrapBox className="mt-8">
          <MyDialogButton 
            label="Quiz History"
            dialogHeader="📊 Quiz History"
            dialogContent={
              <QuizHistory
                history={history}
                topics={data.topics}
                students={students}
                onHistoryChange={saveHistory}
              />
            }
          />

          <MyDialogButton 
            label="Manage Students"
            dialogHeader="👨‍🎓 Student Management"
            dialogContent={
              <StudentManagement
                students={students}
                onStudentsChange={saveStudents}
              />
            }
          />
        </MyFlexWrapBox>
      </div>
    </div>
  );
}

// ============ SUB-COMPONENTS ============

// Student Selector Component
const StudentSelector = ({
  students,
  selectedStudentId,
  selectedStudent,
  onStudentSelect,
  showAddStudent,
  setShowAddStudent,
  newStudent,
  setNewStudent,
  onAddStudent,
}: any) => (
  <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <Users className="w-5 h-5 text-indigo-600" />
        <h2 className="text-lg font-semibold text-gray-800">Student Selection</h2>
      </div>
      <button
        onClick={() => setShowAddStudent(!showAddStudent)}
        className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm font-medium"
      >
        <UserPlus className="w-4 h-4" />
        Add Student
      </button>
    </div>
    
    {showAddStudent && (
      <AddStudentForm
        newStudent={newStudent}
        setNewStudent={setNewStudent}
        onAdd={onAddStudent}
        onCancel={() => setShowAddStudent(false)}
      />
    )}
    
    <div className="flex flex-col md:flex-row gap-4">
      <div className="flex-1 relative">
        <select
          value={selectedStudentId || ""}
          onChange={(e) => onStudentSelect(e.target.value)}
          className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none appearance-none bg-white"
        >
          <option value="">Select a student...</option>
          {students.map((s: Student) => (
            <option key={s.id} value={s.id}>
              {s.name} - {s.grade}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
      
      {selectedStudent && (
        <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-4 py-2 rounded-xl">
          <GraduationCap className="w-4 h-4" />
          <span>Grade: <strong className="text-gray-800">{selectedStudent.grade}</strong></span>
          {selectedStudent.section && (
            <span className="ml-1">Section: <strong className="text-gray-800">{selectedStudent.section}</strong></span>
          )}
        </div>
      )}
    </div>
  </div>
);

// Add Student Form
const AddStudentForm = ({ newStudent, setNewStudent, onAdd, onCancel }: any) => (
  <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-md font-semibold text-gray-800">Add New Student</h3>
      <button onClick={onCancel} className="p-1 hover:bg-gray-200 rounded-lg">
        <X className="w-4 h-4" />
      </button>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <input
        type="text"
        value={newStudent.name}
        onChange={(e) => setNewStudent((p: any) => ({ ...p, name: e.target.value }))}
        placeholder="Full Name *"
        className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
      />
      <input
        type="text"
        value={newStudent.grade}
        onChange={(e) => setNewStudent((p: any) => ({ ...p, grade: e.target.value }))}
        placeholder="Grade * (e.g., 5-A)"
        className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
      />
      <input
        type="text"
        value={newStudent.section}
        onChange={(e) => setNewStudent((p: any) => ({ ...p, section: e.target.value }))}
        placeholder="Section (Optional)"
        className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
      />
      <input
        type="email"
        value={newStudent.email}
        onChange={(e) => setNewStudent((p: any) => ({ ...p, email: e.target.value }))}
        placeholder="Email (Optional)"
        className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition outline-none"
      />
    </div>
    <div className="flex justify-end gap-3 mt-4">
      <button onClick={onCancel} className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition">
        Cancel
      </button>
      <button onClick={onAdd} className="px-6 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition">
        Add Student
      </button>
    </div>
  </div>
);

// Student Grades Overview
const StudentGradesOverview = ({ grades, topics, onViewCertificate }: any) => (
  <div className="mt-6 border-t border-gray-100 pt-4">
    <h3 className="text-sm font-medium text-gray-700 mb-3">Quiz History & Grades</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {grades.map((record: StudentWithGrade) => {
        const topic = topics.find((t: any) => t.id === record.topicId);
        return (
          <div key={`${record.studentId}-${record.topicId}`} className="flex flex-col bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">{topic?.title || "Unknown Topic"}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    record.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {record.passed ? '✓ Passed' : '✗ Failed'}
                  </span>
                  <span className="text-xs text-gray-500">Score: {record.score}%</span>
                </div>
              </div>
              <span className="text-xs text-gray-400">
                {new Date(record.completedAt).toLocaleDateString()}
              </span>
            </div>
            {record.passed && (
              <div className="mt-2 flex justify-end">
                <button
                  onClick={() => onViewCertificate(record)}
                  className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition"
                >
                  <Award className="w-3.5 h-3.5" />
                  View Certificate
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  </div>
);

// Hero Section
const HeroSection = () => (
  <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-6">
    <div className="relative overflow-hidden rounded-2xl sm:rounded-[2rem] bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 text-white shadow-xl">
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_right,white,transparent_60%)]" />
      <div className="relative z-10 p-6 sm:p-10 md:p-14">
        <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center mb-5">
          <Sparkles size={28} className="sm:w-[34px] sm:h-[34px]" />
        </div>
        <h1 className="text-3xl sm:text-5xl md:text-7xl font-black leading-tight mb-4">
          Islamic Learning Quiz
        </h1>
        <p className="text-sm sm:text-base md:text-lg text-white/80 leading-relaxed max-w-2xl">
          Interactive quizzes with certificates, explanations, progress tracking,
          and multiple Islamic learning topics designed for better understanding.
        </p>
      </div>
    </div>
  </div>
);